import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    deleteLookup as apiDeleteLookup,
    getLookups,
    LookupGrid,
    LookupItem,
    lookupRowsToObjects,
    saveLookup as apiSaveLookup,
    LookupName
} from 'api/clientApi';
import { mapModelTypes, mapProjectTypes, ModelType, ProjectType } from 'types/lookups';

/*type LookupsContextValue = {
  lookups: LookupItem[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  refreshLookups: () => Promise<void>;

  // raw finders
  findLookupByName: (lookupName: LookupName |string) => LookupItem | null;

  // consumer-friendly mapped getters
  getLookupById: <T extends Record<string, unknown> = Record<string, unknown>>(
    id: string
  ) => T[];
  getLookupByName: <T extends Record<string, unknown> = Record<string, unknown>>(
    lookupName: string
  ) => T[];

  saveLookup: (id: string | null, lookup: LookupGrid) => Promise<LookupItem | null>;
  //deleteLookup: (lookup: Pick<LookupItem, 'id' | 'lookupName'> | string) => Promise<boolean>;
  deleteLookup: (lookup: Pick<LookupItem, 'lookupName'> | string) => Promise<boolean>;
};*/

type LookupsContextValue = {
    lookups: LookupItem[];
    loading: boolean;
    loaded: boolean;
    error: string | null;

    projectTypes: ProjectType[];
    modelTypes: ModelType[];

    /*findLookupById: (id: string) => LookupItem | null;*/
    findLookupByName: (lookupName: string) => LookupItem | null;
    /*getLookupById: <T extends Record<string, unknown> = Record<string, unknown>>(id: string) => T[];*/
    getLookupByName: <T extends Record<string, unknown> = Record<string, unknown>>(lookupName: string) => T[];

    refreshLookups: () => Promise<void>;
    //saveLookup: (id: string | null, lookup: LookupGrid) => Promise<LookupItem | null>;
    saveLookup: (lookup: LookupGrid) => Promise<LookupItem | null>;
    deleteLookup: (lookup: Pick<LookupItem, 'lookupName'> | string) => Promise<boolean>;
};

type LookupsProviderProps = {
    children: ReactNode;
};

const LookupsContext = createContext<LookupsContextValue | undefined>(undefined);

export function LookupsProvider({ children }: LookupsProviderProps) {
    const [lookups, setLookups] = useState<LookupItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);



    const refreshLookups = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);

            const items = await getLookups();
            console.log('[SANITY] LookupsContext - fetched lookups:', items);
            setLookups(items);
            setLoaded(true);
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Failed to load lookups');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refreshLookups();
    }, [refreshLookups]);

    /*const findLookupById = useCallback(
      (id: string): LookupItem | null =>
        lookups.find((item) => item.id === id) ?? null,
      [lookups]
    );*/

    const findLookupByName = useCallback(
        (lookupName: string): LookupItem | null =>
            lookups.find(
                (item) =>
                    (item.lookupName || '').trim().toLowerCase() ===
                    (lookupName || '').trim().toLowerCase()
            ) ?? null,
        [lookups]
    );

    /*const getLookupById = useCallback(
      <T extends Record<string, unknown> = Record<string, unknown>>(id: string): T[] => {
        const lookup = findLookupById(id);
        return lookup ? (lookupRowsToObjects(lookup) as T[]) : [];
      },
      [findLookupById]
    );*/

    const getLookupByName = useCallback(
        <T extends Record<string, unknown> = Record<string, unknown>>(
            lookupName: string
        ): T[] => {
            const lookup = findLookupByName(lookupName);
            return lookup ? (lookupRowsToObjects(lookup) as T[]) : [];
        },
        [findLookupByName]
    );

    /*const saveLookup = useCallback(
        async (id: string | null, lookup: LookupGrid): Promise<LookupItem | null> => {
            const saved = await apiSaveLookup(id, lookup);
            await refreshLookups();
            return saved;
        },
        [refreshLookups]
    );*/
    const saveLookup = useCallback(
        async (lookup: LookupGrid): Promise<LookupItem | null> => {
            const saved = await apiSaveLookup(lookup);
            await refreshLookups();
            return saved;
        },
        [refreshLookups]
    );

    const deleteLookup = useCallback(
        async (
            lookup: Pick<LookupItem, 'lookupName'> | string
        ): Promise<boolean> => {
            const ok = await apiDeleteLookup(lookup);

            if (ok) {
                await refreshLookups();
            }

            return ok;
        },
        [refreshLookups]
    );

    const projectTypes = useMemo<ProjectType[]>(() => {
        const lookup = findLookupByName('PTYPE');
        console.log(lookups);
        console.log(lookup);
        return lookup ? mapProjectTypes(lookupRowsToObjects(lookup)) : [];
    }, [findLookupByName]);

    const modelTypes = useMemo<ModelType[]>(() => {
        const lookup = findLookupByName('MODL');
        return lookup ? mapModelTypes(lookupRowsToObjects(lookup)) : [];
    }, [findLookupByName]);


    const value = useMemo<LookupsContextValue>(
        () => ({
            lookups,
            loading,
            loaded,
            error,
            projectTypes,
            modelTypes,
            findLookupByName,
            getLookupByName,
            refreshLookups,
            saveLookup,
            deleteLookup
        }),
        [
            lookups,
            loading,
            loaded,
            error,
            projectTypes,
            modelTypes,
            findLookupByName,
            getLookupByName,
            refreshLookups,
            saveLookup,
            deleteLookup
        ]
    );
    /*
        const value = useMemo<LookupsContextValue>(
            () => ({
                lookups,
                loading,
                loaded,
                error,
                refreshLookups,
                //findLookupById,
                findLookupByName,
                //getLookupById,
                getLookupByName,
                saveLookup,
                deleteLookup
            }),
            [
                lookups,
                loading,
                loaded,
                error,
                refreshLookups,
                //findLookupById,
                findLookupByName,
                //getLookupById,
                getLookupByName,
                saveLookup,
                deleteLookup
            ]
        );
    
    
    */
    return <LookupsContext.Provider value={value}>{children}</LookupsContext.Provider>;
}
export function useLookups(): LookupsContextValue {
    const context = useContext(LookupsContext);

    if (!context) {
        throw new Error('useLookups must be used within a LookupsProvider');
    }

    return context;
}